<template>
  <div class="vehicle-filters-root redColor pa-3 pa-md-4">
    <v-row class="vehicle-filters-grid flex-wrap align-center">
    <!--SEARCH DROPDOWN-->
    <v-col
      cols="12"
      :sm="
        $store.getters.role === 'LekoAdmin' ||
        $store.getters.role === 'SuperAdmin'
          ? '2'
          : '3'
      "
    >
      <v-select
        v-model="car.type"
        :items="searchTypes"
        item-title="label"
        item-value="value"
        color="grey-darken-4"
        variant="solo"
        text
        hide-details
      />
    </v-col>

    <!--SEARCH TEXTFIELD-->
    <v-col
      cols="12"
      :sm="
        $store.getters.role === 'LekoAdmin' ||
        $store.getters.role === 'SuperAdmin'
          ? 2
          : 3
      "
    >
      <!-- SEARCH OTHER FIELDS -->
      <v-text-field
        v-model.trim="car.search"
        :label="$t('parts.inventory.search')"
        text
        variant="solo"
        color="grey-darken-4"
        append-inner-icon="mdi-magnify"
        single-line
        hide-details
        @click:append-inner="search"
        @keyup.enter="search"
      />
    </v-col>

    <!-- Mobile: toggle advanced filters (desktop keeps full grid in one row) -->
    <v-col cols="12" class="d-md-none">
      <v-btn
        color="grey-darken-4"
        size="small"
        variant="flat"
        block
        @click="mobileFiltersOpen = !mobileFiltersOpen"
      >
        <v-icon start>
          {{
            mobileFiltersOpen ? "mdi-chevron-up" : "mdi-chevron-down"
          }}
        </v-icon>
        {{ $t("parts.inventory.advanced") }}
      </v-btn>
    </v-col>

    <!--INSTITUTION-->
    <v-col
      v-if="
        ($store.getters.role === 'LekoAdmin' ||
          $store.getters.role === 'SuperAdmin') &&
        showAdvancedFilters
      "
      cols="12"
      sm="12"
      md="3"
      lg="3"
    >
      <v-autocomplete
        v-model="car.institution"
        :items="institutions"
        :loading="loadingInstitution"
        :label="$t('vehicle_entry.institution')"
        item-title="title"
        item-value="_id"
        text
        variant="solo"
        color="red-darken-1"
        hide-details
      />
    </v-col>
    
    <!--PARTS-->
    <v-col
      v-show="showAdvancedFilters"
      cols="12"
      :sm="
        $store.getters.role === 'LekoAdmin' ||
        $store.getters.role === 'SuperAdmin'
          ? '5'
          : '6'
      "
    >
      <v-autocomplete
        v-model="car.part"
        v-model:search="searchPart"
        :loading="loadingPart"
        :items="parts"
        :label="$t('vehicle_entry.part')"
        :item-title="`title.${$store.getters.language}`"
        item-value="partCode"
        text
        variant="solo"
        color="grey-darken-4"
        hide-details
        hide-no-data
      >
        <template #selection="{ item }">
          <div class="d-flex align-center">
            <span>{{ item.raw.title[$store.getters.language] }}</span>
          </div>
        </template>
      </v-autocomplete>
    </v-col>
    <!--BRAND-->
    <v-col v-show="showAdvancedFilters" cols="12" sm="3" md="4" xl="3">
      <v-autocomplete
        v-model="car.brand"
        :items="brands"
        :label="$t('parts.advanced_search.brand')"
        item-title="title"
        autocomplete="off"
        flat
        variant="solo"
        color="red-darken-1"
        hide-details
        return-object
        clearable
        @click:append-inner="brand = null"
      >
        <!-- Custom dropdown item -->
        <template #item="{ props, item }">
          <v-list-item v-bind="props">
            <template #prepend>
              <v-img
                :src="item.raw.logo"
                height="35"
                width="35"
                class="mr-3"
                cover
              />
            </template>
            <!-- <v-list-item-title>{{ item.raw.title }}</v-list-item-title> -->
          </v-list-item>
        </template>

        <!-- Selected value in the field -->
        <template #selection="{ item }">
          <div class="d-flex align-center">
            <v-img
              :src="item.raw.logo"
              height="35"
              width="35"
              class="mr-3"
              cover
            />
            <span>{{ item.raw.title }}</span>
          </div>
        </template>
      </v-autocomplete>
    </v-col>
    <v-col v-show="showAdvancedFilters" cols="12" sm="2" lg="3">
      <v-text-field
        v-model="year"
        :label="$t('parts.advanced_search.year')"
        :rules="[yearRule]"
        text
        variant="solo"
        type="number"
        :max="new Date().getFullYear()"
        oninput="validity.valid || (value = new Date().getFullYear())"
        color="red-darken-1"
        hide-details
      />
    </v-col>
    <!--MODEL-->
    <v-col v-show="showAdvancedFilters" cols="12" sm="4" md="5" xl="4">
      <v-combobox
        v-model="car.model"
        :items="models"
        :label="$t('parts.advanced_search.model')"
        item-title="description"
        item-value="modelCode"
        autocomplete="off"
        variant="solo"
        color="red-darken-1"
        hide-details
        return-object
        clearable
        class="car-search-combobox"
        @click:append-inner="model = null"
      >
        <template #item="{ props, item }">
          <v-list-item v-bind="props">
            <v-list-item-content>
              <v-row>
                <!-- Description -->
                <v-col cols="12" md="4" class="bg-grey-lighten-3 mt-2">
                  <div class="d-flex flex-column">
                    <span class="caption">{{
                      $t("parts.advanced_search.model")
                    }}</span>
                    <span class="font-weight-bold">{{
                      item.raw.description
                    }}</span>
                  </div>
                </v-col>

                <!-- Year range -->
                <v-col
                  cols="12"
                  md="8"
                  class="bg-grey-lighten-3 mt-2"
                >
                  <div class="d-flex flex-column">
                    <span class="caption">{{
                      $t("parts.advanced_search.year")
                    }}</span>
                    <span class="font-weight-bold">
                      {{ formatMonthYear(item.raw.startingDate) }} -
                      {{ formatMonthYear(item.raw.endingDate) }}
                    </span>
                  </div>
                </v-col>
              </v-row>
            </v-list-item-content>
          </v-list-item>
        </template>

        <!-- Selected value -->
        <template #selection="{ item }">
          <div
            class="car-detail-selection d-flex justify-between align-center pa-2 w-100"
          >
            <div class="car-detail-col d-flex flex-column pr-2">
              <span class="caption">{{
                $t("parts.advanced_search.model")
              }}</span>
              <span class="font-weight-bold">{{
                item.raw.description
              }}</span>
            </div>
            <div class="car-detail-col d-flex flex-column">
              <span class="caption">{{
                $t("parts.advanced_search.year")
              }}</span>
              <span class="font-weight-bold">
                {{ formatMonthYear(item.raw.startingDate) }} -
                {{ formatMonthYear(item.raw.endingDate) }}
              </span>
            </div>
          </div>
        </template>
      </v-combobox>
    </v-col>
    <!--ENGINE-->
    <v-col v-show="showAdvancedFilters" cols="12" sm="4" md="2" xl="1">
      <v-autocomplete
        v-model="car.engine"
        :items="versions"
        :loading="loadingVersion"
        :label="$t('parts.advanced_search.engine')"
        item-title="engineCode"
        autocomplete="off"
        text
        variant="solo"
        color="red-darken-1"
        hide-details
        :append-icon="
          car.engine === null ? 'arrow_drop_down' : 'keyboard_backspace'
        "
        @click:append="
          car.engine = null;
          car.version = null;
          if (car.model) getVersions(car.model.brandCode, car.model.modelCode);
        "
      />
    </v-col>
    <!--VERSION-->
    <v-col v-show="showAdvancedFilters" cols="12" sm="6" md="6" xl="6">
      <v-combobox
        v-model="car.version"
        :items="versions"
        :label="$t('parts.advanced_search.version')"
        item-title="fullDescription"
        flat
        variant="solo"
        color="red-darken-1"
        hide-details
        return-object
        clearable
        class="car-search-combobox car-search-version-combobox"
        @click:append-inner="version = null"
      >
        <!-- Dropdown items -->
        <template #item="{ item, props }">
          <v-list-item v-bind="props">
            <div
              class="d-sm-none car-version-detail-item bg-grey-lighten-3 mt-2 pa-3 w-100"
            >
              <div
                class="car-version-detail-row car-version-detail-row--dropdown"
              >
                <div
                  class="car-version-detail-col car-version-detail-col--main"
                >
                  <span class="caption">{{
                    $t("parts.advanced_search.version")
                  }}</span>&nbsp;
                  <span class="font-weight-bold">{{
                    item.raw.description
                  }}</span>
                </div>
                <div class="car-version-detail-col">
                  <span class="caption">{{
                    $t("parts.advanced_search.year")
                  }}</span>&nbsp;
                  <span class="font-weight-bold">
                    {{ formatMonthYear(item.raw.startingDate) }} -
                    {{ formatMonthYear(item.raw.endingDate) }}
                  </span>
                </div>
                <div class="car-version-detail-col">
                  <span class="caption">{{
                    $t("parts.advanced_search.body")
                  }}</span>&nbsp;
                  <span class="font-weight-bold">{{
                    item.raw.numberOfDoors
                  }}</span>
                </div>
                <div class="car-version-detail-col">
                  <span class="caption">{{
                    $t("parts.advanced_search.power")
                  }}</span>&nbsp;
                  <span class="font-weight-bold">
                    {{ item.raw.power ? item.raw.power.kw : "" }} kw,
                  </span>
                </div>
                <div class="car-version-detail-col">
                  <span class="caption">{{
                    $t("parts.advanced_search.engine")
                  }}</span>&nbsp;
                  <span class="font-weight-bold">{{
                    item.raw.engineCode
                  }}</span>
                </div>
                <div class="car-version-detail-col">
                  <span class="caption">{{
                    $t("parts.advanced_search.supply.supply")
                  }}</span>&nbsp;
                  <span class="font-weight-bold">{{
                    getSupplyLocale(item.raw.supplyCode)
                  }}</span>
                </div>
              </div>
            </div>
                    <v-row class="d-none d-sm-flex pa-3 bg-grey-lighten-3 mt-2">
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.version")
                          }}</span>
                          <span class="font-weight-bold">{{
                            item.raw.description
                          }}</span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.year")
                          }}</span>
                          <span class="font-weight-bold">
                            {{ formatMonthYear(item.raw.startingDate) }} -
                            {{ formatMonthYear(item.raw.endingDate) }}
                          </span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.body")
                          }}</span>
                          <span class="font-weight-bold">{{
                            item.raw.numberOfDoors
                          }}</span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.power")
                          }}</span>
                          <span class="font-weight-bold">
                            {{ item.raw.power ? item.raw.power.kw : "" }} kw,
                          </span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.engine")
                          }}</span>
                          <span class="font-weight-bold">{{
                            item.raw.engineCode
                          }}</span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.supply.supply")
                          }}</span>
                          <span class="font-weight-bold">{{
                            getSupplyLocale(item.raw.supplyCode)
                          }}</span>
                        </v-row>
                      </v-col>
                    </v-row>
          </v-list-item>
        </template>

        <!--SELECTED-->
        <template #selection="{ item }">
          <div class="car-version-detail-selection car-detail-selection w-100 d-sm-none">
            <div class="car-version-detail-row">
              <div
                class="car-version-detail-col car-version-detail-col--main"
              >
                <span class="caption">
                  {{ $t("parts.advanced_search.version") }}
                </span>
                <span class="font-weight-bold">{{
                  item.raw.description
                }}</span>
              </div>
              <div class="car-version-detail-col">
                <span class="caption">{{
                  $t("parts.advanced_search.year")
                }}</span>
                <span class="font-weight-bold">
                  {{ formatMonthYear(item.raw.startingDate) }} -
                  {{ formatMonthYear(item.raw.endingDate) }}
                </span>
              </div>
              <div class="car-version-detail-col">
                <span class="caption">{{
                  $t("parts.advanced_search.body")
                }}</span>
                <span class="font-weight-bold">{{
                  item.raw.numberOfDoors
                }}</span>
              </div>
              <div class="car-version-detail-col">
                <span class="caption">{{
                  $t("parts.advanced_search.power")
                }}</span>
                <span class="font-weight-bold">
                  {{ item.raw.power ? item.raw.power.kw : " " }} kw,
                </span>
              </div>
              <div class="car-version-detail-col">
                <span class="caption">{{
                  $t("parts.advanced_search.engine")
                }}</span>
                <span class="font-weight-bold">{{
                  item.raw.engineCode
                }}</span>
              </div>
              <div class="car-version-detail-col">
                <span class="caption">{{
                  $t("parts.advanced_search.supply.supply")
                }}</span>
                <span class="font-weight-bold">{{
                  getSupplyLocale(item.raw.supplyCode)
                }}</span>
              </div>
            </div>
          </div>
                  <v-row class="pa-2 d-none d-sm-flex">
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">
                          {{ $t("parts.advanced_search.version") }}
                        </span>
                        <span class="font-weight-bold">{{
                          item.raw.description
                        }}</span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.year")
                        }}</span>
                        <span class="font-weight-bold">
                          {{ formatMonthYear(item.raw.startingDate) }} -
                          {{ formatMonthYear(item.raw.endingDate) }}
                        </span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.body")
                        }}</span>
                        <span class="font-weight-bold">{{
                          item.raw.numberOfDoors
                        }}</span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.power")
                        }}</span>
                        <span class="font-weight-bold">
                          {{ item.raw.power ? item.raw.power.kw : " " }} kw,
                        </span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.engine")
                        }}</span>
                        <span class="font-weight-bold">{{
                          item.raw.engineCode
                        }}</span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.supply.supply")
                        }}</span>
                        <span class="font-weight-bold">{{
                          getSupplyLocale(item.raw.supplyCode)
                        }}</span>
                      </v-row>
                    </v-col>
                  </v-row>
        </template>
      </v-combobox>
    </v-col>
<!--LOCATION-->
    <v-col
      v-show="showAdvancedFilters"
      cols="12"
      md="4"
      lg="4"
      sm="4"
    >
      <v-autocomplete
        v-model="car.location"
        :items="carLocations"
        :loading="loadingCarLocations"
        :label="$t('vehicle_entry.info.location')"
        autocomplete="off"
        item-title="name"
        item-value="_id"
        text
        variant="solo"
        color="grey-darken-4"
        hide-details
        clearable
      />
    </v-col>
    <!--NUMBER OF KM-->
    <v-col v-show="showAdvancedFilters" cols="12" md="4">
      <v-row align="center" class="flex-wrap">
        <v-col cols="12" sm="8" lg="8">
          <v-range-slider
            v-model="car.km"
            :max="1000000"
            :min="0"
            :step="1000"
            color="grey-darken-4"
          />
        </v-col>
        <v-col cols="12" sm="4" lg="4" class="text-center text-sm-start">
          <span class="pl-sm-3 pr-2"> {{ car.km[0] }} - {{ car.km[1] }} km </span>
        </v-col>
      </v-row>
    </v-col>

    <!--STARTING DATE-->
    <v-col v-show="showAdvancedFilters" cols="12" sm="6" md="4">
      <v-menu
        v-model="startDateMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        max-width="290"
      >
        <template #activator="{ props }">
          <v-text-field
            v-model="car.startDate"
            :label="$t('parts.advanced_search.starting_date')"
            :value="formatDateToDisplay(car.startDate)"
            :hint="$t('parts.advanced_search.select_starting_date')"
            variant="solo"
            flat
            color="black"
            persistent-hint
            prepend-icon="mdi-calendar"
            readonly
            v-bind="props"
          />
        </template>

        <v-date-picker
          v-model="car.startDate"
          color="red-darken-3"
          :max="car.endDate || null"
          @update:model-value="startDateMenu = false"
        />
      </v-menu>
    </v-col>
    <!--ENDING DATE-->
    <v-col v-show="showAdvancedFilters" cols="12" sm="6" md="4">
      <v-menu
        v-model="endDateMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        max-width="290"
      >
        <template #activator="{ props }">
          <v-text-field
            v-model="car.endDate"
            :label="$t('parts.advanced_search.ending_date')"
            :hint="$t('parts.advanced_search.select_ending_date')"
            variant="solo"
            flat
            color="black"
            persistent-hint
            prepend-icon="mdi-calendar"
            readonly
            v-bind="props"
          />
        </template>

        <v-date-picker
          v-model="car.endDate"
          color="red-darken-3"
          :min="car.startDate || null"
          @update:model-value="endDateMenu = false"
        />
      </v-menu>
    </v-col>

    <!--BUTTONS-->
    <v-col v-show="showAdvancedFilters" cols="12">
      <v-row class="justify-center align-center pb-2 flex-wrap ga-2">
        <v-btn dark color="grey-darken-4" class="mx-1" @click="clear">
          {{ $t("parts.advanced_search.clear") }}
          <v-icon end> mdi-notification-clear-all </v-icon>
        </v-btn>

        <v-btn dark color="grey-darken-4" class="mx-1" @click="search">
          {{ $t("parts.advanced_search.search") }}
          <v-icon end> mdi-magnify </v-icon>
        </v-btn>

        <v-btn
          v-if="
            $store.getters.institutionDisplayFeatures.infoCarCad &&
            car.brand &&
            car.model &&
            car.version
          "
          dark
          color="grey-darken-4"
          class="mx-1"
          @click="openInfoCarCad"
        >
          {{ $t("parts.inventory.cad") }}
        </v-btn>
      </v-row>
    </v-col>
  </v-row>
  </div>
</template>

<script>
import moment from "moment";

import getRequest from "../../api/getRequest";

export default {
  filters: {
    moment(date) {
      moment.locale(localStorage.getItem("language"));
      const valid = moment(date).isValid();
      if (valid) return moment(date).format("MM/YYYY");
      return "";
    },
  },
  data: () => ({
    loading: false,
    loadingBrand: false,
    loadingModel: false,
    loadingVersion: false,
    loadingPart: false,
    loadingInstitution: false,
    loadingCarLocations: false,
    institutions: [],
    carLocations: [],
    parts: [],
    brands: [],
    models: [],
    versions: [],
    searchPart: null,
    car: {
      type: "id",
      search: null,
      institution: null,
      location: null,
      part: null,
      brand: null,
      model: null,
      version: null,
      engine: null,
      km: [0, 1000000],
      startDate: null,
      endDate: null,
    },
    startDateMenu: false,
    endDateMenu: false,
    searchTypes: [
      { label: "ID", value: "id" },
      { label: "PRA", value: "pra" },
      { label: "VIN", value: "vin" },
      { label: "Other", value: "other" },
    ],
    disableAutoAsign: false,
    yearRule: "",
    year: null,
    mobileFiltersOpen: false,
  }),
  computed: {
    showAdvancedFilters() {
      return this.$vuetify.display.mdAndUp || this.mobileFiltersOpen;
    },
  },
  watch: {
    searchPart(value) {
      if (value) {
        this.loadingPart = true;
        getRequest(
          "/info-car/parts",
          `title=${value}&language=${this.$store.getters.language}`,
        )
          .then((response) => {
            this.parts = response.parts;
          })
          .catch(() => {})
          .finally(() => {
            this.loadingPart = false;
          });
      } else {
        this.car.part = null;
      }
    },
    "car.institution"() {
      this.car.location = null;
      this.getCarLocations();
    },
    "car.brand": {
      handler(value) {
        if (value && !this.disableAutoAsign) {
          this.models = [];
          this.car.model = null;
          this.versions = [];
          this.car.version = null;
          this.car.engine = null;
          this.getModels(value.brandCode);
        }
      },
    },
    "car.model": {
      handler(value) {
        if (value && !this.disableAutoAsign) {
          this.versions = [];
          this.car.version = null;
          this.car.engine = null;
          this.setYearRule(value.startingDate, value.endingDate);
          this.getVersions(value.brandCode, value.modelCode);
        }
      },
    },
    "car.version": {
      handler(value) {
        if (value) {
          this.$emit("selected", this.car);
          this.setYearRule(value.startingDate, value.endingDate);
        } else {
          this.$emit("selected", null);
        }
      },
    },
    "car.engine": {
      handler(value) {
        this.versions = this.versions.reduce((acc, v) => {
          if (v.engineCode === value) acc.push(v);
          return acc;
        }, []);
      },
    },
    year(value) {
      if (value) {
        // this.$refs.form.validate();
        // filter models
        if (value.length === 4) {
          // Filter only when year is valid (4 characters long)
          this.filterModels();
          this.filterVersions();
        } else if (value.length !== 4 && this.brand && this.brand.brandCode) {
          // Reset models.
          this.models = this.getModels(this.brand.brandCode);
        }
      }
    },
    "$i18n.locale"() {
      this.getLocale();
    },
  },
  created() {
    this.getLocale();
    this.getBrands();
    this.getInstitutions();
    this.getCarLocations();
  },
  mounted() {
    // Set initial year rule.
    this.setYearRule();
  },
  methods: {
    getLocale() {
      this.searchTypes[3].label = this.$t("sales.other");
    },
    setYearRule(startingDate, endingDate) {
      const startingYear = startingDate
        ? new Date(startingDate).getFullYear()
        : 1900;
      const endingYear = endingDate
        ? new Date(endingDate).getFullYear()
        : new Date().getFullYear();
      this.yearRule = (v) =>
        (v >= startingYear && v <= endingYear) ||
        this.$t("parts.advanced_search.year_error");
    },
    getBrands() {
      this.loadingBrand = true;
      getRequest("/info-car/brands")
        .then((response) => {
          this.brands = response.brands;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingBrand = false;
        });
    },
    getModels(brandCode) {
      this.loadingModel = true;
      getRequest("/info-car/models", `brand_code=${brandCode}`)
        .then((response) => {
          this.models = response.models;
          this.filterModels();
        })
        .catch(() => {})
        .finally(() => {
          this.loadingModel = false;
        });
    },
    getVersions(brandCode, modelCode) {
      this.loadingVersion = true;
      getRequest(
        "/info-car/versions",
        `brand_code=${brandCode}&model_code=${modelCode}`,
      )
        .then((response) => {
          this.versions = response.versions;
          this.filterVersions();
        })
        .catch(() => {})
        .finally(() => {
          this.loadingVersion = false;
        });
    },
    filterModels() {
      if (
        this.year &&
        this.year.length === 4 &&
        this.models &&
        this.models.length > 0
      ) {
        this.models = this.models.filter((model) => {
          const sYear = model.startingDate
            ? new Date(model.startingDate).getFullYear()
            : 1900;
          const eYear = model.endingDate
            ? new Date(model.endingDate).getFullYear()
            : new Date().getFullYear();

          return this.year >= sYear && this.year <= eYear;
        });
      }
    },
    filterVersions() {
      if (this.year) {
        // filter by year
        this.versions = this.versions.filter((version) => {
          const sYear = version.startingDate
            ? new Date(version.startingDate).getFullYear()
            : 1900;
          const eYear = version.endingDate
            ? new Date(version.endingDate).getFullYear()
            : new Date().getFullYear();

          return this.year >= sYear && this.year <= eYear;
        });
      }
    },
    async forceSetFilters(brand, model) {
      this.disableAutoAsign = true;
      try {
        const brandsResult = await getRequest("/info-car/brands");
        const modelsResult = await getRequest(
          "/info-car/models",
          `brand_code=${brand}`,
        );
        const selectedBrand = brandsResult.brands.find(
          (b) => b.brandCode === parseInt(brand, 10),
        );
        const selectedModel = modelsResult.models.find(
          (m) =>
            m.brandCode === parseInt(brand, 10) &&
            m.modelCode === parseInt(model, 10),
        );

        this.brands = brandsResult.brands;
        this.car.brand = selectedBrand;
        this.models = modelsResult.models;
        this.car.model = selectedModel;

        this.search();
      } catch {}
    },

    clear() {
      this.car = {
        type: "id",
        search: null,
        institution: null,
        location: null,
        part: null,
        brand: null,
        model: null,
        version: null,
        engine: null,
        km: [0, 1000000],
        startDate: null,
        endDate: null,
      };

      this.brands = [];
      this.models = [];
      this.versions = [];

      this.disableAutoAsign = false;

      this.getBrands();
      this.search();
    },
    search() {
      this.$emit("search", {
        type: this.car.type ? this.car.type : null,
        search: this.car.search ? this.car.search : null,
        institution: this.car.institution ? this.car.institution : null,
        location: this.car.location ? this.car.location : null,
        part: this.car.part ? this.car.part : null,
        km: this.car.km,
        brand: this.car.brand ? this.car.brand.brandCode : null,
        model: this.car.model ? this.car.model.modelCode : null,
        engine: this.car.engine ? this.car.engine : null,
        version: this.car.version ? this.car.version.versionCode : null,
        startDate: this.car.startDate ? this.car.startDate : null,
        endDate: this.car.endDate ? this.car.endDate : null,
      });
    },
    formatDateToDisplay(date) {
      if (!date) return "";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0"); // 0-indexed
      const year = d.getFullYear();
      return `${year}-${month}-${day}`;
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
    formatMonthYear(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // months are 0-indexed
      const year = date.getUTCFullYear();
      return `${month}/${year}`;
    },
    getSupplyLocale(supply) {
      switch (supply) {
        case "B":
          return this.$t("parts.advanced_search.supply.petrol");
        case "D":
          return this.$t("parts.advanced_search.supply.diesel");
        case "G":
          return this.$t("parts.advanced_search.supply.gas");
        default:
          return supply;
      }
    },

    openInfoCarCad() {
      this.$emit("infocar-cad", {
        brandCode: this.car.brand.brandCode,
        modelCode: this.car.model.modelCode,
        versionCode: this.car.version.versionCode,
      });
    },

    getCarLocations() {
      this.loadingCarLocations = true;
      const isAdmin =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin";
      const institutionId = isAdmin
        ? this.car.institution || ""
        : this.$store.getters.institutionId;
      getRequest(
        "/car-location",
        `role=${this.$store.getters.role}&institution_id=${institutionId}&limit=500&page=1&sort_by=name&descending=false`,
      )
        .then((response) => {
          this.carLocations = response.carLocations || [];
        })
        .catch(() => {
          this.carLocations = [];
        })
        .finally(() => {
          this.loadingCarLocations = false;
        });
    },
    getInstitutions() {
      this.loadingInstitution = true;
      getRequest("/institution/")
        .then((response) => {
          this.institutions = response.institutions;
          this.loadingInstitution = false;
        })
        .catch(() => {
          this.loadingInstitution = false;
        });
    },
  },
};
</script>

<style scoped>
.vehicle-filters-root {
  overflow-x: hidden;
}

.vehicle-filters-grid :deep(.v-field) {
  min-width: 0;
}

/* Model/version combobox mobile layout */
@media (max-width: 959px) {
  .vehicle-filters-root .car-version-detail-selection {
    width: 100%;
    padding: 0.375rem 0.25rem;
  }

  .vehicle-filters-root .car-version-detail-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    width: 100%;
    gap: 0.75rem 1rem;
  }

  .vehicle-filters-root .car-version-detail-col {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
    flex: 1 1 0;
  }

  .vehicle-filters-root .car-version-detail-col .caption {
    line-height: 1.25;
    margin-bottom: 0.125rem;
  }

  .vehicle-filters-root .car-version-detail-col .font-weight-bold {
    line-height: 1.4;
    word-break: break-word;
  }

  .vehicle-filters-root .car-search-combobox :deep(.v-field) {
    --v-field-padding-top: 10px;
    --v-field-padding-bottom: 10px;
    height: auto !important;
    min-height: 3.5rem;
  }

  .vehicle-filters-root .car-search-version-combobox :deep(.v-field) {
    --v-field-padding-top: 12px;
    --v-field-padding-bottom: 12px;
    min-height: 4.5rem;
  }

  .vehicle-filters-root .car-search-combobox :deep(.v-field__field) {
    height: auto !important;
    min-height: 3.5rem;
    align-items: flex-start;
  }

  .vehicle-filters-root .car-search-version-combobox :deep(.v-field__field) {
    min-height: 4.5rem;
  }

  .vehicle-filters-root .car-search-combobox :deep(.v-field__input) {
    white-space: normal;
    overflow: visible;
    align-items: flex-start;
    flex-wrap: wrap;
    min-height: unset;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .vehicle-filters-root .car-search-version-combobox :deep(.v-field__input) {
    display: block !important;
    height: auto !important;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .vehicle-filters-root .car-search-combobox :deep(.v-combobox__selection) {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    width: 100%;
    max-width: 100%;
  }

  .vehicle-filters-root .car-search-version-combobox :deep(.v-combobox__selection) {
    display: block !important;
    width: 100%;
    position: static !important;
    transform: none !important;
  }

  .vehicle-filters-root .car-detail-selection {
    overflow: visible;
    width: 100%;
  }


  .vehicle-filters-root .car-version-detail-row,
  .vehicle-filters-root .car-version-detail-row--dropdown {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem 0.875rem;
    flex-wrap: unset;
  }

  .vehicle-filters-root .car-version-detail-col {
    flex: unset;
    padding: 0;
  }

  .vehicle-filters-root .car-version-detail-col--main {
    grid-column: 1 / -1;
    padding-bottom: 0.625rem;
    margin-bottom: 0.25rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .vehicle-filters-root .car-version-detail-col--main .font-weight-bold {
    line-height: 1.45;
  }

  .vehicle-filters-root .car-detail-col {
    flex: 1 1 calc(50% - 0.5rem);
    max-width: none;
    min-width: 0;
    padding: 0;
    word-break: break-word;
  }

  .vehicle-filters-root .car-detail-selection.d-flex {
    flex-wrap: wrap;
    gap: 0.625rem 0.75rem;
  }
}
</style>
